import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import "./css/WorldClass.css";

const WorldClass = () => {
  // State to track which card is flipped
  const [flippedCard, setFlippedCard] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(0);

  // Data structure for the cards and their flip content
  const domainData = {
    "Technology & Software Development": {
      icon: "chart-bar",
      title: "Technology ",
      subCategories: [
        {
          title: "Full Stack Development",
          topics: [
            "MERN/MEAN stack projects",
            "API development & integration",
            "CI/CD pipeline setup",
            "Microservices architecture",
          ],
        },
        {
          title: "Data Science & Machine Learning",
          topics: [
            "Predictive analytics models",
            "NLP & text classification",
            "Deep learning for image recognition",
            "MLOps and model deployment",
          ],
        },
        {
          title: "DevOps & Cloud Engineering",
          topics: [
            "Infrastructure as Code (IaC)",
            "CI/CD using Jenkins, GitHub Actions",
            "Cloud cost optimization",
            "Monitoring with Prometheus/Grafana",
          ],
        },
        {
          title: "Cybersecurity & Ethical Hacking",
          topics: [
            "Vulnerability assessment",
            "Penetration testing",
            "Network security auditing",
            "Threat modeling & risk analysis",
          ],
        },
        // {
        //   title: "Deep Learning",
        //   topics: ["CNN", "RNN", "LSTM", "GANs"],
        // },
        // {
        //   title: "Data Visualisation",
        //   topics: ["Tableau", "Power BI", "D3.js", "Data Storytelling"],
        // },
      ],
    },
    "Sustainability & Impact": {
      icon: "monitor",
      title: "Sustainability & Impact",
      subCategories: [
        {
          title: "ESG Strategy & Reporting",
          topics: [
            "ESG metric frameworks (e.g., SASB, GRI)",
            "ESG audits & disclosure reports",
            "Stakeholder engagement strategies",
            "ESG risk mitigation",
          ],
        },
        {
          title: "Climate Tech & Green Innovation",
          topics: [
            "Carbon footprint tracking tools",
            "Clean energy systems (solar, wind)",
            "Sustainable IoT solutions",
            "Carbon credit trading platforms",
          ],
        },
        {
          title: "Circular Economy & Waste Management",
          topics: [
            "E-waste recycling systems",
            "Zero-waste process planning",
            "Resource recovery modeling",
            "Sustainable packaging solutions",
          ],
        },
        {
          title: "Sustainable Supply Chain Management",
          topics: [
            "Supplier ESG risk assessment",
            "Green logistics and routing",
            "Life cycle impact analysis",
            "Traceability and blockchain integration",
          ],
        },
      ],
    },
    "Finance & FinTech": {
      icon: "briefcase",
      title: "Finance & FinTech",
      subCategories: [
        {
          title: "Financial Planning & Analysis (FP&A)",
          topics: [
            "Budget forecasting",
            "Variance analysis",
            "Executive dashboards",
            "Scenario modeling",
          ],
        },
        {
          title: "Investment Banking & Equity Research",
          topics: [
            "DCF and LBO modeling",
            "M&A analysis",
            "Industry benchmarking",
            "Pitch deck creation",
          ],
        },
        {
          title: "Accounting & Compliance",
          topics: [
            "IFRS/GAAP implementation",
            "Audit preparation",
            "Tax planning & filing",
            "Internal controls setup",
          ],
        },
        {
          title: "FinTech Product Management",
          topics: [
            "Digital payments integration",
            "Lending platform design",
            "API monetization strategies",
            "RegTech and compliance automation",
          ],
        },
      ],
    },
  };

  // Function to handle card flip
  const handleCardFlip = (domain) => {
    if (flippedCard === domain) {
      setFlippedCard(null); // flip back
    } else {
      setFlippedCard(domain); // flip to show details
    }
  };

  // Automatic slider movement
  useEffect(() => {
    const interval = setInterval(() => {
      setSliderPosition((prevPosition) => {
        if (prevPosition <= -200) {
          return 0;
        }
        return prevPosition - 1;
      });
    }, 30);

    return () => clearInterval(interval);
  }, []);

  // JSX for rendering icons based on name
  const renderIcon = (iconName) => {
    switch (iconName) {
      case "chart-bar":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="18" y="3" width="4" height="18"></rect>
            <rect x="10" y="8" width="4" height="13"></rect>
            <rect x="2" y="13" width="4" height="8"></rect>
          </svg>
        );
      case "monitor":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        );
      case "briefcase":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="worldclass-section">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="section-heading">
              World-class professionals with in-demand skills in top domains
            </h2>
          </div>
        </div>

        <div className="row justify-content-center">
          {/* Map through the domain data to create cards */}
          {Object.keys(domainData).map((domain) => (
            <div className="col-lg-4 col-md-6 mb-4" key={domain}>
              <div className="flip-card-container">
                <div
                  className={`flip-card ${
                    flippedCard === domain ? "flipped" : ""
                  }`}
                  onClick={() => handleCardFlip(domain)}
                >
                  {/* Front of the card */}
                  <div className="flip-card-front">
                    <div className="domain-card">
                      <div className="domain-header text-center mb-4">
                        <div className="icon-wrapper">
                          {renderIcon(domainData[domain].icon)}
                        </div>
                        <h3 className="domain-title">
                          {domainData[domain].title}
                        </h3>
                      </div>
                      <div className="subcategory-preview">
                        {domainData[domain].subCategories.map(
                          (subCategory, index) => (
                            <div className="subcategory-item" key={index}>
                              <span>{subCategory.title}</span>
                              <ArrowRight size={16} className="arrow-icon" />
                            </div>
                          )
                        )}
                      </div>
                      <div className="flip-prompt">
                        <span>Click to see details</span>
                      </div>
                    </div>
                  </div>

                  {/* Back of the card */}
                  <div className="flip-card-back">
                    <div className="back-header">
                      <h3 className="back-title">
                        {domainData[domain].title} Skills
                      </h3>
                    </div>
                    <div className="back-content">
                      {domainData[domain].subCategories.map(
                        (subCategory, index) => (
                          <div className="back-subcategory" key={index}>
                            <h5 className="back-subtitle">
                              {subCategory.title}
                            </h5>
                            <ul className="topic-list">
                              {subCategory.topics.map((topic, topicIndex) => (
                                <li key={topicIndex}>{topic}</li>
                              ))}
                            </ul>
                          </div>
                        )
                      )}
                    </div>
                    <div className="back-footer">
                      <button className="btn-flip-back">
                        <ArrowRight size={16} className="back-icon" /> Back
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorldClass;
